import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const WHATSAPP_NUMBER = "919392599671";

const initial = { name: "", phone: "", email: "", message: "" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatWhatsAppMessage = ({ name, phone, email, message }) =>
  [
    "Hi JobNex, I'd like to get in touch.",
    "",
    `Name: ${name.trim()}`,
    `Phone: ${phone.trim()}`,
    `Email: ${email.trim()}`,
    "",
    "Message:",
    message.trim(),
  ].join("\n");

const saveLead = (form) =>
  fetch(`${API_BASE}/api/leads`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(form),
  });

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    const whatsappText = formatWhatsAppMessage(form);

    try {
      try {
        await navigator.clipboard.writeText(whatsappText);
      } catch {
        // Clipboard may be blocked; WhatsApp still opens with the message.
      }

      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappText)}`;
      window.open(whatsappUrl, "_blank", "noopener,noreferrer");

      saveLead(form).catch(() => {});

      toast.success("Opening WhatsApp - your details are copied. Send the message to reach us.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-12 gap-y-14 gap-x-8">
          <div className="col-span-12 md:col-span-5">
            <p className="label-eyebrow text-[#143628] mb-5">Get in touch</p>
            <h2 className="font-serif-display text-4xl sm:text-6xl leading-[0.95] tracking-tight">
              Let&rsquo;s talk about <em className="italic text-[#143628]">your next role.</em>
            </h2>
            <p className="text-[#4A4A48] mt-7 leading-relaxed text-lg max-w-md">
              Tell us where you&rsquo;re headed. A placement specialist will respond within one business day with the next steps.
            </p>

            <div className="mt-10 pt-8 border-t border-[#1C1C1A]/10 space-y-4 text-sm">
              <div>
                <p className="label-eyebrow text-[#4A4A48]">Email</p>
                <p className="mt-1 text-[#1C1C1A]">support@jobnex.co.in</p>
              </div>
              <div>
                <p className="label-eyebrow text-[#4A4A48]">Response time</p>
                <p className="mt-1 text-[#1C1C1A]">Within 1 business day</p>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white border border-[#1C1C1A]/10 p-8 sm:p-10"
              noValidate
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="lead-name" className="label-eyebrow text-[#4A4A48]">
                    Name
                  </Label>
                  <Input
                    id="lead-name"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="mt-2 h-12 rounded-none border-[#1C1C1A]/15 focus-visible:ring-0 focus-visible:border-[#143628]"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lead-phone" className="label-eyebrow text-[#4A4A48]">
                    Phone number
                  </Label>
                  <Input
                    id="lead-phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+91 98xxx xxxxx"
                    className="mt-2 h-12 rounded-none border-[#1C1C1A]/15 focus-visible:ring-0 focus-visible:border-[#143628]"
                    required
                  />
                </div>
              </div>

              <div className="mt-5">
                <Label htmlFor="lead-email" className="label-eyebrow text-[#4A4A48]">
                  Email
                </Label>
                <Input
                  id="lead-email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@domain.com"
                  className="mt-2 h-12 rounded-none border-[#1C1C1A]/15 focus-visible:ring-0 focus-visible:border-[#143628]"
                  required
                />
              </div>

              <div className="mt-5">
                <Label htmlFor="lead-message" className="label-eyebrow text-[#4A4A48]">
                  Message
                </Label>
                <Textarea
                  id="lead-message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us about your goals, current role, and what you’re looking for."
                  className="mt-2 rounded-none border-[#1C1C1A]/15 focus-visible:ring-0 focus-visible:border-[#143628] min-h-[140px]"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="mt-8 w-full rounded-none bg-[#143628] hover:bg-[#0D241A] text-[#F9F9F7] h-14 text-base font-medium tracking-tight"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send message
                    <Send className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>

              <p className="text-xs text-[#4A4A48] mt-4">
                By submitting, you&apos;ll open WhatsApp with your details pre-filled to message us at +91 93925 99671.
              </p>            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
